import React, { useState, useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { IoDocumentOutline, IoAdd, IoSettingsOutline } from "react-icons/io5";
import { MdOutlineDelete } from "react-icons/md";
import {
  getRoomInclusions,
  getAvailableInclusionsForRoom,
  getAvailableInclusions,
  getSubcategories,
  addBOQToInclusionFormData,
  deleteBOQ,
  deleteInclusion,
  updateBOQ,
  uploadBOQImage,
  deleteFile,
} from "../../services/budgetService";
import { getApiData } from "../../utils/APIHelper";
import ScopeBOQItemCard from "../../components/RoomLevelBOQ/components/ScopeBOQItemCard";
import ScopeBOQItemRow from "../../components/RoomLevelBOQ/components/ScopeBOQItemRow";
import ScopeInlineAddItemRow from "../../components/RoomLevelBOQ/components/ScopeInlineAddItemRow";
import ScopeInlineEditBoqRow from "../../components/RoomLevelBOQ/components/ScopeInlineEditBoqRow";
import AddItemModal from "../NewProjectDetails/components/Budgets/AddItemModal";
import DeleteBoqModal from "../NewProjectDetails/components/Budgets/DeleteBoqModal";
import DeleteInclusionModal from "../NewProjectDetails/components/Budgets/DeleteInclusionModal";
import { renderCurrencyIconColored } from "../../utils/CurrencyUtils";
import { formattedAmountWithoutAed } from "../../utils/CommonFunction";
import { requiresLeadTime } from "../../constants/boq";

const normalizeRoomName = (name) =>
  String(name || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");

const Spinner = ({ size = 20, color = "#4D6BF6" }) => (
  <div
    style={{
      width: size,
      height: size,
      border: `2px solid ${color}33`,
      borderTopColor: color,
      borderRadius: "50%",
      animation: "reno-spin 0.8s linear infinite",
      display: "inline-block",
    }}
  />
);

const spinnerKeyframes = `@keyframes reno-spin { to { transform: rotate(360deg); } }`;

const RoomScope = ({
  projectId,
  roomId,
  roomData,
  projectDetails,
  isMobile,
  isTablet,
  isSiteVisit = false,
  isSiteEstimatesRoute = false,
  viewOnly = false,
}) => {
  const projectType = projectDetails?.project_type;
  const roomName = roomData?.room_name || roomData?.name || "";
  const roomNameForApi = normalizeRoomName(roomName);
  const showUnitCostInput = !isSiteVisit;

  const [inclusions, setInclusions] = useState([]);
  const [availableInclusions, setAvailableInclusions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addingInclusion, setAddingInclusion] = useState(null);

  const [expandedInclusions, setExpandedInclusions] = useState({});
  const [subcategoriesMap, setSubcategoriesMap] = useState({});
  const [loadingSubcategories, setLoadingSubcategories] = useState({});

  const [pendingItems, setPendingItems] = useState({});
  const [savingItems, setSavingItems] = useState({});

  const [inlineEditingBoqId, setInlineEditingBoqId] = useState(null);
  const [inlineEditingBoqInclusion, setInlineEditingBoqInclusion] = useState(null);

  const [isAddItemModalOpen, setIsAddItemModalOpen] = useState(false);
  const [selectedInclusionForItem, setSelectedInclusionForItem] = useState(null);
  const [isAddingItem, setIsAddingItem] = useState(false);
  const [isEditingItem, setIsEditingItem] = useState(false);
  const [editingBoq, setEditingBoq] = useState(null);

  const [isDeleteBoqModalOpen, setIsDeleteBoqModalOpen] = useState(false);
  const [boqToDelete, setBoqToDelete] = useState(null);
  const [isDeleteInclusionModalOpen, setIsDeleteInclusionModalOpen] = useState(false);
  const [inclusionToDelete, setInclusionToDelete] = useState(null);

  const [lightboxImage, setLightboxImage] = useState(null);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const inclusionsResponse = await getRoomInclusions(projectId, roomId);
      if (inclusionsResponse?.success) {
        setInclusions(inclusionsResponse.inclusions || []);
      } else {
        setInclusions([]);
      }
      if (projectType) {
        try {
          let availableResponse;
          if (roomNameForApi) {
            availableResponse = await getAvailableInclusionsForRoom(projectType, roomNameForApi);
          } else {
            availableResponse = await getAvailableInclusions(projectType);
          }
          if (availableResponse?.success) {
            setAvailableInclusions(availableResponse.inclusions || []);
          }
        } catch (err) {
          console.log("Could not load available inclusions", err);
          setAvailableInclusions([]);
        }
      }
    } catch (err) {
      console.error("Error loading room scope data:", err);
      setError(err);
      toast.error("Failed to load room data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (projectId && roomId) {
      loadData();
    }
  }, [projectId, roomId]);

  const addedInclusionNames = useMemo(
    () => (inclusions || []).map((i) => i.name),
    [inclusions]
  );

  const fetchSubcategories = async (inclusionId, inclusionName) => {
    if (!inclusionName || !projectType) return;
    try {
      setLoadingSubcategories((prev) => ({ ...prev, [inclusionId]: true }));
      const data = await getSubcategories(inclusionName, projectType, roomNameForApi);
      if (data?.success && data.subcategories) {
        const options = data.subcategories.map((item) => ({
          label: item.subcategory_name,
          value: item.subcategory_name,
        }));
        setSubcategoriesMap((prev) => ({ ...prev, [inclusionId]: options }));
      } else {
        setSubcategoriesMap((prev) => ({ ...prev, [inclusionId]: [] }));
      }
    } catch (err) {
      console.error("Error fetching subcategories:", err);
      setSubcategoriesMap((prev) => ({ ...prev, [inclusionId]: [] }));
    } finally {
      setLoadingSubcategories((prev) => ({ ...prev, [inclusionId]: false }));
    }
  };

  const toggleInclusion = async (inclusionId, inclusionName) => {
    const isExpanding = !expandedInclusions[inclusionId];
    setExpandedInclusions((prev) => ({ ...prev, [inclusionId]: isExpanding }));
    if (isExpanding && !isMobile && !subcategoriesMap[inclusionId]) {
      await fetchSubcategories(inclusionId, inclusionName);
    }
  };

  const getNextItemNameForSubcategory = (inclusionId, subcategoryValue) => {
    const escaped = String(subcategoryValue).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const regex = new RegExp(`^${escaped} \\d+$`);
    const sameBase = Object.values(pendingItems).filter(
      (item) =>
        item.inclusionId === inclusionId &&
        (item.item_name === subcategoryValue || regex.test(item.item_name))
    );
    return sameBase.length === 0 ? subcategoryValue : `${subcategoryValue} ${sameBase.length}`;
  };

  const handleSubcategoryClick = (inclusionId, subcategory) => {
    const uniqueKey = `${inclusionId}_${subcategory.value}_${Date.now()}`;
    const itemName = getNextItemNameForSubcategory(inclusionId, subcategory.value);
    setPendingItems((prev) => ({
      ...prev,
      [uniqueKey]: {
        id: uniqueKey,
        inclusionId,
        item_name: itemName,
        subcategory: subcategory.value,
        boq_type: "Materials",
        quantity: "",
        days: "",
        material_unit: null,
        supply_apply: null,
        specification: "",
        unit_cost: "",
        lead_time: "",
      },
    }));
  };

  const handleUpdatePendingItem = (key, updates) =>
    setPendingItems((prev) => ({ ...prev, [key]: { ...prev[key], ...updates } }));

  const handleCancelPendingItem = (key) => {
    setPendingItems((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const getPendingItemsForInclusion = (inclusionId) =>
    Object.entries(pendingItems)
      .filter(([k]) => k.startsWith(`${inclusionId}_`))
      .map(([key, item]) => ({ key, ...item }));

  const handleSavePendingItem = async (key, inclusion) => {
    const pendingItem = pendingItems[key];
    if (!pendingItem) return;
    if (!pendingItem.item_name) {
      toast.error("Item name is required");
      return;
    }
    if (pendingItem.boq_type === "Materials") {
      const qty = pendingItem.quantity || "";
      if (qty === "" || isNaN(qty) || parseInt(qty) < 0) {
        toast.error("Quantity is required and must be 0 or a positive number");
        return;
      }
    }
    if (pendingItem.boq_type === "Activities") {
      const days = pendingItem.days || "";
      if (days === "" || isNaN(days) || parseInt(days) < 0) {
        toast.error("Days is required and must be 0 or a positive number");
        return;
      }
    }
    if (pendingItem.boq_type === "Materials" && !pendingItem.material_unit) {
      toast.error("Material Unit is required");
      return;
    }
    if (!pendingItem.supply_apply) {
      toast.error("Supply/Apply is required");
      return;
    }
    // Lead Time mandatory when Supply / Supply and Apply (reno-web rule).
    if (requiresLeadTime(pendingItem.supply_apply)) {
      const lt = String(pendingItem.lead_time ?? "").trim();
      if (lt === "") {
        toast.error("Please enter Lead Time (days)");
        return;
      }
      if (!/^\d+$/.test(lt)) {
        toast.error("Lead Time must be a positive integer");
        return;
      }
    }
    if (!pendingItem.specification) {
      toast.error("Specification is required");
      return;
    }
    if (showUnitCostInput && isSiteEstimatesRoute) {
      const uc = pendingItem.unit_cost;
      if (uc === "" || uc == null) {
        toast.error("Unit cost is required");
        return;
      }
      if (Number(uc) === 0) {
        toast.error("Enter a valid cost.");
        return;
      }
    } else if (
      showUnitCostInput &&
      pendingItem.unit_cost !== "" &&
      pendingItem.unit_cost != null
    ) {
      if (Number(pendingItem.unit_cost) === 0) {
        toast.error("Enter a valid cost.");
        return;
      }
    }
    setSavingItems((prev) => ({ ...prev, [key]: true }));
    try {
      const formData = new FormData();
      formData.append("name", pendingItem.item_name);
      formData.append("type", pendingItem.boq_type);
      formData.append("subcategory", pendingItem.subcategory);
      formData.append("supply_type", pendingItem.supply_apply);
      formData.append("specification", pendingItem.specification);
      formData.append(
        "material_unit",
        pendingItem.boq_type === "Materials" ? pendingItem.material_unit ?? "" : ""
      );
      if (pendingItem.boq_type === "Materials") {
        formData.append("quantity", pendingItem.quantity || "");
        formData.append("days", "");
      } else {
        formData.append("days", pendingItem.days || "");
        formData.append("quantity", "");
      }
      formData.append("room_id", roomId);
      formData.append("inclusion_name", inclusion.name);
      if (
        requiresLeadTime(pendingItem.supply_apply) &&
        pendingItem.lead_time != null &&
        String(pendingItem.lead_time).trim() !== ""
      ) {
        formData.append("lead_time", String(pendingItem.lead_time).trim());
      }
      // No inline image attachment — image uploaded post-save via the
      // read-only BOQ row/card.
      const data = await addBOQToInclusionFormData(projectId, inclusion.id, formData);

      if (data?.success) {
        const newBoqId = data.boq_id ?? data.id ?? data.data?.id ?? data.boq?.id;
        if (
          showUnitCostInput &&
          newBoqId != null &&
          (isSiteEstimatesRoute || (pendingItem.unit_cost ?? "") !== "")
        ) {
          try {
            const trimmed = String(pendingItem.unit_cost).trim();
            const boqType = pendingItem.boq_type || "Materials";
            const updatePayload = {
              name: pendingItem.item_name,
              boq_type: boqType,
              subcategory: pendingItem.subcategory ?? "",
              supply_type: pendingItem.supply_apply,
              specification: pendingItem.specification ?? "",
              ...(boqType === "Activities"
                ? {
                    days: pendingItem.days || null,
                    manpower_rate: trimmed,
                    qty: null,
                    material_unit_price: null,
                  }
                : {
                    material_unit: pendingItem.material_unit,
                    qty: pendingItem.quantity || null,
                    material_unit_price: trimmed,
                    days: null,
                    manpower_rate: null,
                  }),
            };
            await updateBOQ(projectId, newBoqId, updatePayload);
          } catch (e) {
            console.error("Item added but unit cost update failed", e);
          }
        }
        toast.success(`Successfully added ${pendingItem.item_name} to ${inclusion.name}`);
        handleCancelPendingItem(key);
        await loadData();
      } else {
        toast.error("Failed to add item");
      }
    } catch (err) {
      console.error("Error adding item:", err);
      toast.error("Failed to add item");
    } finally {
      setSavingItems((prev) => ({ ...prev, [key]: false }));
    }
  };

  const handleAfterScopeInlineEditSave = async () => {
    setInlineEditingBoqId(null);
    setInlineEditingBoqInclusion(null);
    await loadData();
  };

  const handleCancelInlineEditBoq = () => {
    setInlineEditingBoqId(null);
    setInlineEditingBoqInclusion(null);
  };

  const handleAddInclusion = async (inclusionName) => {
    setAddingInclusion(inclusionName);
    try {
      const response = await getApiData(
        `api/project/${projectId}/room/${roomId}/inclusion`,
        "POST",
        { name: inclusionName }
      );
      if (response?.success) {
        toast.success(`Successfully added ${inclusionName} to room`);
        await loadData();
      } else {
        toast.error("Failed to add inclusion");
      }
    } catch (err) {
      console.error("Error adding inclusion:", err);
      toast.error("Failed to add inclusion");
    } finally {
      setAddingInclusion(null);
    }
  };

  const handleOpenAddItemModal = (inclusion) => {
    setSelectedInclusionForItem(inclusion);
    setIsEditingItem(false);
    setEditingBoq(null);
    setIsAddItemModalOpen(true);
  };

  const handleCloseAddItemModal = () => {
    setIsAddItemModalOpen(false);
    setSelectedInclusionForItem(null);
    setIsEditingItem(false);
    setEditingBoq(null);
  };

  const handleEditBoq = (boq, inclusion) => {
    if (isMobile) {
      setEditingBoq(boq);
      setSelectedInclusionForItem(inclusion);
      setIsEditingItem(true);
      setIsAddItemModalOpen(true);
    } else {
      setInlineEditingBoqId(boq.id);
      setInlineEditingBoqInclusion(inclusion);
    }
  };

  const handleDeleteBoq = (boq, inclusionId) => {
    setBoqToDelete({ ...boq, inclusionId });
    setIsDeleteBoqModalOpen(true);
  };

  const handleConfirmDeleteBoq = async (boqId) => {
    try {
      const response = await deleteBOQ(projectId, boqId);
      if (response?.success) {
        toast.success("BOQ item deleted successfully");
        await loadData();
        setIsDeleteBoqModalOpen(false);
        setBoqToDelete(null);
      } else {
        toast.error("Failed to delete BOQ item");
      }
    } catch (err) {
      console.error("Error deleting BOQ:", err);
      toast.error("Failed to delete BOQ item");
    }
  };

  const handleCloseDeleteBoqModal = () => {
    setIsDeleteBoqModalOpen(false);
    setBoqToDelete(null);
  };

  const handleDeleteInclusion = (inclusion) => {
    setInclusionToDelete(inclusion);
    setIsDeleteInclusionModalOpen(true);
  };

  const handleConfirmDeleteInclusion = async (inclusionId) => {
    try {
      const response = await deleteInclusion(inclusionId);
      if (response?.success) {
        toast.success("Inclusion deleted successfully");
        await loadData();
        setIsDeleteInclusionModalOpen(false);
        setInclusionToDelete(null);
      } else {
        toast.error(response?.message || "Failed to delete inclusion");
      }
    } catch (err) {
      console.error("Error deleting inclusion:", err);
      toast.error("Failed to delete inclusion");
    }
  };

  const handleCloseDeleteInclusionModal = () => {
    setIsDeleteInclusionModalOpen(false);
    setInclusionToDelete(null);
  };

  const handleAddItem = async (itemData) => {
    if (!selectedInclusionForItem) return;
    setIsAddingItem(true);
    try {
      const formData = new FormData();
      formData.append("name", itemData.item_name);
      formData.append("type", itemData.boq_type);
      formData.append(
        "subcategory",
        itemData.subcategory?.label || itemData.subcategory || ""
      );
      formData.append(
        "supply_type",
        itemData.supply_apply?.label || itemData.supply_apply || ""
      );
      formData.append("specification", itemData.specification || "");
      formData.append(
        "material_unit",
        itemData.boq_type === "Activities"
          ? ""
          : itemData.material_unit?.value || itemData.material_unit || ""
      );
      if (itemData.boq_type === "Materials") {
        formData.append("quantity", itemData.quantity || "");
      } else if (itemData.boq_type === "Activities") {
        formData.append("days", itemData.days || "");
      }
      formData.append("room_id", roomId);
      formData.append("inclusion_name", selectedInclusionForItem.name);
      if (itemData.image) formData.append("budget_image", itemData.image);
      // AddItemModal already coerces lead_time → int|null based on the
      // supply_apply rule; forward when non-nullish so backend stores it.
      if (
        itemData.lead_time != null &&
        String(itemData.lead_time).trim() !== ""
      ) {
        formData.append("lead_time", String(itemData.lead_time).trim());
      }

      const response = await addBOQToInclusionFormData(
        projectId,
        selectedInclusionForItem.id,
        formData
      );
      if (response?.success) {
        toast.success(`Successfully added item to ${selectedInclusionForItem.name}`);
        await loadData();
        handleCloseAddItemModal();
      } else {
        toast.error("Failed to add item");
      }
    } catch (err) {
      console.error("Error adding item:", err);
      toast.error("Failed to add item");
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleUpdateBoq = async (itemData) => {
    if (!editingBoq || !selectedInclusionForItem) return;
    setIsAddingItem(true);
    try {
      const boqType = itemData.boq_type;
      const unitCostFallback =
        boqType === "Activities" ? editingBoq.manpower_rate : editingBoq.unit_cost;
      const trimmedUnitCost = String(itemData.unit_cost ?? "").trim();
      const unitCostValue =
        trimmedUnitCost !== ""
          ? trimmedUnitCost
          : unitCostFallback != null
          ? String(unitCostFallback)
          : null;

      const updateData = {
        name: itemData.item_name,
        boq_type: boqType,
        subcategory: itemData.subcategory?.label || itemData.subcategory || "",
        supply_type: itemData.supply_apply?.label || itemData.supply_apply,
        specification: itemData.specification,
        lead_time: itemData.lead_time != null ? itemData.lead_time : null,
        ...(boqType === "Activities"
          ? {
              days: itemData.days || null,
              manpower_rate: unitCostValue,
              qty: null,
              material_unit_price: null,
            }
          : {
              material_unit: itemData.material_unit?.value || itemData.material_unit,
              qty: itemData.quantity || null,
              material_unit_price: unitCostValue,
              days: null,
              manpower_rate: null,
            }),
      };
      const response = await updateBOQ(projectId, editingBoq.id, updateData);
      if (response?.success) {
        toast.success("BOQ item updated successfully");
        await loadData();
        handleCloseAddItemModal();
      } else {
        toast.error("Failed to update BOQ item");
      }
    } catch (err) {
      console.error("Error updating BOQ:", err);
      toast.error("Failed to update BOQ item");
    } finally {
      setIsAddingItem(false);
    }
  };

  const handleDeleteBoqImage = async (imageId) => {
    try {
      const response = await deleteFile(imageId);
      if (response?.success) {
        toast.success("Image deleted successfully");
        setEditingBoq((prev) => ({ ...prev, image: [] }));
        await loadData();
      } else {
        toast.error("Failed to delete image");
      }
    } catch (err) {
      console.error("Error deleting BOQ image:", err);
      toast.error("Failed to delete image");
    }
  };

  const handleUploadBoqImage = async (file) => {
    if (!editingBoq) return;
    try {
      const response = await uploadBOQImage(editingBoq.id, file);
      if (response?.success) {
        toast.success("Image uploaded successfully");
        const images = response.images ?? response.data ?? [];
        const first = images[0];
        if (first) {
          setEditingBoq((prev) => ({
            ...prev,
            image: [{ image: first.image ?? first.path, id: first.id }],
          }));
        }
        await loadData();
      } else {
        toast.error("Failed to upload image");
      }
    } catch (err) {
      console.error("Error uploading BOQ image:", err);
      toast.error("Failed to upload image");
    }
  };

  if (loading) {
    return (
      <>
        <style>{spinnerKeyframes}</style>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 16,
            padding: "48px 0",
          }}
        >
          <Spinner size={40} />
          <div style={{ color: "#6b7280", fontSize: 14 }}>Loading room inclusions...</div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: "48px 0" }}>
        <div style={{ color: "#DC2626", fontSize: 14 }}>
          Error loading room inclusions. Please try again.
        </div>
      </div>
    );
  }

  return (
    <>
      <style>{spinnerKeyframes}</style>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 24,
        }}
      >
        {inclusions.length === 0 ? (
          <div style={{ textAlign: "center", padding: "48px 0" }}>
            <IoDocumentOutline
              style={{ width: 48, height: 48, color: "#9ca3af", margin: "0 auto 16px" }}
            />
            <div style={{ color: "#111827", fontSize: 18, fontWeight: 500, marginBottom: 8 }}>
              Room BOQs
            </div>
            <div style={{ color: "#6b7280", fontSize: 14 }}>
              No BOQs found for this room
            </div>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {inclusions.map((inclusion) => (
              <div
                key={inclusion.id}
                style={{
                  backgroundColor: "#fff",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  overflow: "hidden",
                }}
              >
                {/* Header */}
                <div style={{ padding: 24, borderBottom: "1px solid #e5e7eb" }}>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                    }}
                  >
                    <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
                      <IoSettingsOutline
                        style={{ width: 20, height: 20, color: "#4b5563" }}
                      />
                      <div>
                        <div
                          style={{
                            fontWeight: 500,
                            color: "#111827",
                            fontSize: 16,
                          }}
                        >
                          {inclusion.name}
                        </div>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 4,
                          }}
                        >
                          <span style={{ color: "#6b7280", fontSize: 12 }}>
                            {inclusion.boq_count} items
                          </span>
                          <span
                            style={{
                              padding: "2px 8px",
                              fontSize: 11,
                              fontWeight: 500,
                              backgroundColor: "#ffedd5",
                              color: "#9a3412",
                              borderRadius: 999,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 3,
                            }}
                          >
                            {inclusion.inclusion_amount != null &&
                            inclusion.inclusion_amount !== "" ? (
                              <>
                                {renderCurrencyIconColored("black-small")}
                                {formattedAmountWithoutAed(inclusion.inclusion_amount)}
                              </>
                            ) : (
                              "-"
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      {/* {!viewOnly && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteInclusion(inclusion);
                          }}
                          style={{
                            background: "transparent",
                            border: "none",
                            padding: 8,
                            color: "#9ca3af",
                            cursor: "pointer",
                            display: "flex",
                            alignItems: "center",
                          }}
                          title="Delete inclusion"
                        >
                          <MdOutlineDelete style={{ width: 20, height: 20 }} />
                        </button>
                      )} */}
                      <button
                        onClick={() => toggleInclusion(inclusion.id, inclusion.name)}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: 8,
                          color: "#9ca3af",
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <svg
                          style={{
                            width: 20,
                            height: 20,
                            transition: "transform 0.2s",
                            transform: expandedInclusions[inclusion.id]
                              ? "rotate(0deg)"
                              : "rotate(180deg)",
                          }}
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth={2}
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        >
                          <path d="M6 15l6-6 6 6" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>

                {/* Body */}
                {expandedInclusions[inclusion.id] && (
                  <div
                    style={{
                      padding: 24,
                      display: "flex",
                      flexDirection: "column",
                      gap: 12,
                    }}
                  >
                    {(inclusion.boqs || []).map((boq) =>
                      !isMobile &&
                      inlineEditingBoqId === boq.id &&
                      inlineEditingBoqInclusion?.id === inclusion.id ? (
                        <ScopeInlineEditBoqRow
                          key={`edit-${boq.id}`}
                          boq={boq}
                          inclusion={inclusion}
                          projectId={projectId}
                          showUnitCostInput={showUnitCostInput}
                          isSiteEstimatesRoute={isSiteEstimatesRoute}
                          onBudgetUpdated={handleAfterScopeInlineEditSave}
                          onCancel={handleCancelInlineEditBoq}
                          isSaving={false}
                        />
                      ) : isMobile ? (
                        <ScopeBOQItemCard
                          key={boq.id}
                          boq={boq}
                          inclusionId={inclusion.id}
                          projectId={projectId}
                          isSiteVisit={isSiteVisit}
                          isSiteEstimatesRoute={isSiteEstimatesRoute}
                          onBudgetUpdated={loadData}
                          onEdit={
                            !viewOnly ? () => handleEditBoq(boq, inclusion) : undefined
                          }
                          onDelete={
                            !viewOnly
                              ? () => handleDeleteBoq(boq, inclusion.id)
                              : undefined
                          }
                          showUnitCostInput={showUnitCostInput}
                        />
                      ) : (
                        <ScopeBOQItemRow
                          key={boq.id}
                          boq={boq}
                          isSiteVisit={isSiteVisit}
                          viewOnly={viewOnly}
                          onEdit={() => handleEditBoq(boq, inclusion)}
                          onDelete={() => handleDeleteBoq(boq, inclusion.id)}
                          onImageClick={(image) => setLightboxImage(image)}
                          onBudgetUpdated={loadData}
                        />
                      )
                    )}

                    {/* Mobile: Add Item button */}
                    {/* {!viewOnly && isMobile && (
                      <button
                        onClick={() => handleOpenAddItemModal(inclusion)}
                        style={{
                          width: "100%",
                          marginTop: 16,
                          padding: "8px 16px",
                          fontSize: 14,
                          fontWeight: 500,
                          color: "#2563eb",
                          backgroundColor: "#fff",
                          border: "1px solid #d1d5db",
                          borderRadius: 6,
                          cursor: "pointer",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          gap: 8,
                        }}
                      >
                        <IoAdd style={{ width: 16, height: 16 }} />
                        Add Item to {inclusion.name}
                      </button>
                    )} */}

                    {/* Desktop: pending items + subcategory chips */}
                    {!viewOnly && !isMobile && (
                      <>
                        {getPendingItemsForInclusion(inclusion.id).map((item) => (
                          <ScopeInlineAddItemRow
                            key={item.key}
                            pendingItem={item}
                            showUnitCostInput={showUnitCostInput}
                            isSiteVisit={isSiteVisit}
                            onUpdate={(updates) =>
                              handleUpdatePendingItem(item.key, updates)
                            }
                            onSave={() => handleSavePendingItem(item.key, inclusion)}
                            onCancel={() => handleCancelPendingItem(item.key)}
                            isSaving={savingItems[item.key]}
                          />
                        ))}
                        {/* <div style={{ marginTop: 16 }}>
                          <div
                            style={{ color: "#6b7280", fontSize: 13, marginBottom: 8 }}
                          >
                            Add items:
                          </div>
                          {loadingSubcategories[inclusion.id] ? (
                            <div
                              style={{ display: "flex", alignItems: "center", gap: 8 }}
                            >
                              <Spinner size={14} />
                              <span style={{ color: "#6b7280", fontSize: 12 }}>
                                Loading subcategories...
                              </span>
                            </div>
                          ) : (subcategoriesMap[inclusion.id] || []).length > 0 ? (
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                              {(subcategoriesMap[inclusion.id] || []).map((subcat) => (
                                <button
                                  key={subcat.value}
                                  onClick={() =>
                                    handleSubcategoryClick(inclusion.id, subcat)
                                  }
                                  style={{
                                    padding: "6px 12px",
                                    fontSize: 12,
                                    fontWeight: 500,
                                    borderRadius: 8,
                                    border: "1px solid #d1d5db",
                                    backgroundColor: "#fff",
                                    color: "#4b5563",
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 4,
                                  }}
                                >
                                  <IoAdd style={{ width: 12, height: 12 }} />
                                  {subcat.label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span style={{ color: "#9ca3af", fontSize: 12 }}>
                              No subcategories available
                            </span>
                          )}
                        </div> */}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Available inclusions */}
        {/* {!viewOnly && (
          <div style={{ marginTop: 24 }}>
            <div
              style={{
                marginBottom: 16,
                color: "#111827",
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              Available Inclusions for {roomName || "this room"}
            </div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
              {availableInclusions.map((inclusion) => {
                const isAlreadyAdded = addedInclusionNames.includes(
                  inclusion.inclusion_name
                );
                const isAdding = addingInclusion === inclusion.inclusion_name;
                return (
                  <button
                    key={inclusion.inclusion_id}
                    onClick={() =>
                      !isAlreadyAdded && handleAddInclusion(inclusion.inclusion_name)
                    }
                    disabled={isAlreadyAdded || isAdding}
                    style={{
                      fontSize: 12,
                      fontWeight: 500,
                      padding: ".7vh 1vw",
                      borderRadius: 8,
                      textTransform: "none",
                      backgroundColor: isAlreadyAdded ? "#f5f5f5" : "#ffffff",
                      color: isAlreadyAdded ? "#999" : "#666",
                      border: "1px solid #d7d8d9",
                      cursor:
                        isAlreadyAdded || isAdding ? "not-allowed" : "pointer",
                      opacity: isAlreadyAdded || isAdding ? 0.7 : 1,
                    }}
                  >
                    {isAdding ? (
                      <span
                        style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
                      >
                        <Spinner size={12} />
                        <span>Adding...</span>
                      </span>
                    ) : isAlreadyAdded ? (
                      `✓ ${inclusion.inclusion_name}`
                    ) : (
                      `+ ${inclusion.inclusion_name}`
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        )} */}

        {/* Lightbox */}
        {lightboxImage && (
          <div
            onClick={() => setLightboxImage(null)}
            style={{
              position: "fixed",
              inset: 0,
              backgroundColor: "rgba(0,0,0,0.8)",
              zIndex: 9999,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            <img
              src={lightboxImage}
              alt=""
              style={{ maxWidth: "90%", maxHeight: "90%" }}
            />
          </div>
        )}

        {/* Mobile Add/Edit modal */}
        {!viewOnly && (
          <AddItemModal
            isOpen={isAddItemModalOpen}
            onClose={handleCloseAddItemModal}
            inclusionName={selectedInclusionForItem?.name}
            onSubmit={isEditingItem ? handleUpdateBoq : handleAddItem}
            loading={isAddingItem}
            isEditing={isEditingItem}
            editingBoq={editingBoq}
            onDeleteImage={handleDeleteBoqImage}
            onUploadImage={handleUploadBoqImage}
            rooms={[]}
            selectedRoom={null}
            project={projectDetails}
            isSiteVisit={isSiteVisit}
            roomNameForSubcategories={roomNameForApi}
          />
        )}

        {!viewOnly && (
          <DeleteBoqModal
            open={isDeleteBoqModalOpen}
            onClose={handleCloseDeleteBoqModal}
            onSuccess={handleCloseDeleteBoqModal}
            boqData={boqToDelete}
            projectId={projectId}
            inclusionId={boqToDelete?.inclusionId}
            onDeleteBoq={handleConfirmDeleteBoq}
          />
        )}

        {!viewOnly && (
          <DeleteInclusionModal
            open={isDeleteInclusionModalOpen}
            onClose={handleCloseDeleteInclusionModal}
            inclusionData={inclusionToDelete}
            onDeleteInclusion={handleConfirmDeleteInclusion}
          />
        )}
      </div>
    </>
  );
};

export default RoomScope;
